import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Users, 
  Target, 
  Mail, 
  BarChart3, 
  Zap,
  Check,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Shield,
  Clock,
  TrendingUp,
  Brain,
  Globe,
  Star,
  Play
} from 'lucide-react';

export function LandingPage() {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const features = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Powered Lead Enrichment",
      description: "Automatically enrich leads with company data, contact info, tech stack, and market intelligence using advanced AI."
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "AI Intro Email Generation",
      description: "Generate personalized, high-converting intro emails based on your company profile and lead insights."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Tailored Insights & Scoring",
      description: "Get AI-powered fit scores, talking points, and strategic recommendations for each lead."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Complete Contact Management",
      description: "Manage leads, contacts, companies, tasks, and calendar events in one unified platform."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "GDPR Compliant",
      description: "Full data privacy compliance with consent management, data export, and deletion rights."
    }
  ];

  const plans = [
    {
      name: "Base",
      plan_key: "base",
      price: "29",
      period: "month",
      description: "Perfect for small teams getting started",
      features: [
        "10 users included",
        "500 leads maximum",
        "AI lead enrichment",
        "Contact & company management",
        "Tasks & calendar",
        "Workflow automation",
        "GDPR compliance tools",
        "Email & chat support"
      ],
      limitations: [
        "No AI Intro Email",
        "Basic reporting"
      ],
      recommended: false,
      gradient: "from-gray-50 to-gray-100"
    },
    {
      name: "Tier 1",
      plan_key: "tier1",
      price: "59",
      period: "month",
      description: "Best for growing sales teams",
      features: [
        "25 users included",
        "2,500 leads maximum",
        "AI lead enrichment",
        "✨ AI Intro Email generation",
        "✨ Tailored insights & scoring",
        "Contact & company management",
        "Tasks & calendar",
        "Advanced workflow automation",
        "GDPR compliance tools",
        "Priority email & chat support",
        "Advanced analytics"
      ],
      limitations: [],
      recommended: true,
      gradient: "from-blue-50 to-indigo-100"
    },
    {
      name: "Tier 2",
      plan_key: "tier2",
      price: "89",
      period: "month",
      description: "For larger teams & enterprises",
      features: [
        "50 users included",
        "2,500 leads maximum",
        "AI lead enrichment",
        "✨ AI Intro Email generation",
        "✨ Tailored insights & scoring",
        "Contact & company management",
        "Tasks & calendar",
        "Advanced workflow automation",
        "GDPR compliance tools",
        "Premium support with dedicated account manager",
        "Advanced analytics & reporting",
        "Custom integrations available"
      ],
      limitations: [],
      recommended: false,
      gradient: "from-purple-50 to-pink-100"
    }
  ];

  const faqs = [
    {
      question: "How does the AI lead enrichment work?",
      answer: "I-Match uses advanced AI (GPT-4) combined with web search to automatically gather comprehensive data about your leads including company information, key contacts, technology stack, recent news, funding, and market positioning. The enrichment happens in the background and provides you with 10 detailed categories of insights."
    },
    {
      question: "What is AI Intro Email and which plans include it?",
      answer: "AI Intro Email is a premium feature that automatically generates personalized, professional introduction emails based on your company profile and the lead's enriched data. It's included in Tier 1 and Tier 2 plans, but not in the Base plan. The AI considers your value propositions, the lead's industry, recent company news, and pain points to craft compelling outreach."
    },
    {
      question: "Can I upgrade or downgrade my plan anytime?",
      answer: "Yes! You can upgrade or downgrade your plan at any time. When upgrading, you'll get immediate access to new features and higher limits. When downgrading, changes take effect at the end of your current billing period, and you won't lose any data."
    },
    {
      question: "What happens when I reach my user or lead limit?",
      answer: "When you approach your limits, you'll receive notifications. If you hit the limit, you can either upgrade to a higher tier or purchase add-ons (extra users at €5-8/user/month or lead top-ups at €15-30 per package). You won't lose access to existing data, but you won't be able to add more until you upgrade."
    },
    {
      question: "Is my data secure and GDPR compliant?",
      answer: "Absolutely! I-Match is fully GDPR compliant with built-in tools for consent management, data export, and deletion requests. Your data is encrypted, stored securely, and completely isolated from other tenants in our multi-tenant architecture. We never share your data with third parties."
    },
    {
      question: "Do you offer a free trial?",
      answer: "Yes! We offer a 14-day free trial on all plans so you can test all features before committing. No credit card required to start your trial."
    },
    {
      question: "Can I import my existing leads?",
      answer: "Yes! I-Match supports CSV import so you can easily bring in your existing lead database. You can map your fields to our system, and we'll help you get set up quickly."
    },
    {
      question: "What kind of support do you provide?",
      answer: "All plans include email and chat support. Tier 1 gets priority support, and Tier 2 includes a dedicated account manager. We also provide comprehensive documentation, video tutorials, and onboarding assistance."
    },
    {
      question: "Can I integrate I-Match with other tools?",
      answer: "Currently, I-Match includes built-in integrations with Stripe for billing. We're actively working on additional integrations. Tier 2 customers can request custom integrations which we'll evaluate for implementation."
    },
    {
      question: "How does billing work?",
      answer: "Billing is monthly via Stripe. You'll be charged on the same day each month. We accept all major credit cards. Invoices are sent automatically, and you can view your billing history in your account settings."
    }
  ];

  const demoSteps = [
    {
      step: 1,
      title: "Import Your Leads",
      description: "Upload your lead list via CSV or add leads manually. I-Match accepts any lead data you have.",
      image: "📊"
    },
    {
      step: 2,
      title: "AI Enriches Automatically",
      description: "Our AI automatically enriches each lead with company data, contacts, tech stack, recent news, and market intelligence.",
      image: "🤖"
    },
    {
      step: 3,
      title: "Review Insights & Scores",
      description: "Get AI-powered fit scores, talking points, potential concerns, and recommended approaches for each lead.",
      image: "📈"
    },
    {
      step: 4,
      title: "Generate Personalized Emails",
      description: "Click one button and AI generates a personalized intro email based on your company profile and the lead's data.",
      image: "✉️"
    },
    {
      step: 5,
      title: "Track & Manage",
      description: "Manage follow-ups with tasks, calendar events, workflows, and pipeline tracking all in one place.",
      image: "✅"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Target className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                I-Match CRM
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition">Features</a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition">Pricing</a>
              <a href="#demo" className="text-gray-700 hover:text-blue-600 transition">Demo</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 transition">FAQ</a>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowLoginModal(true)}
                className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register?plan=tier1')}
                className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-medium transition shadow-lg shadow-blue-500/50"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              <span>AI-Powered Lead Enhancement</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
              Transform Leads Into
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Revenue Opportunities
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
              I-Match CRM uses advanced AI to enrich your leads with deep insights, generate personalized emails, 
              and help you close deals faster. All in one powerful platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/register?plan=tier1')}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-semibold transition shadow-xl shadow-blue-500/50 flex items-center gap-2 text-lg"
              >
                Start Your 14-Day Free Trial
                <ArrowRight className="w-5 h-5" />
              </button>
              
              <button
                onClick={() => document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-lg hover:border-blue-600 hover:text-blue-600 font-semibold transition flex items-center gap-2 text-lg"
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </button>
            </div>
            
            <div className="mt-12 flex items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span>14-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">10K+</div>
              <div className="text-gray-600 mt-2">Leads Enriched</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">95%</div>
              <div className="text-gray-600 mt-2">Enrichment Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">3x</div>
              <div className="text-gray-600 mt-2">Faster Outreach</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">50%</div>
              <div className="text-gray-600 mt-2">Higher Response Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need to <span className="text-blue-600">Win More Deals</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful features designed to help sales teams work smarter, not harder
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 bg-gradient-to-br from-white to-blue-50 rounded-xl border border-blue-100 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent <span className="text-blue-600">Pricing</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that fits your team. Upgrade or downgrade anytime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative rounded-2xl p-8 ${
                  plan.recommended 
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-2xl scale-105 border-4 border-blue-600' 
                    : `bg-gradient-to-br ${plan.gradient} border-2 border-gray-200`
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Star className="w-4 h-4" />
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className={`text-center mb-6 ${plan.recommended ? 'text-white' : 'text-gray-900'}`}>
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <p className={`text-sm ${plan.recommended ? 'text-blue-100' : 'text-gray-600'}`}>
                    {plan.description}
                  </p>
                </div>

                <div className={`text-center mb-8 ${plan.recommended ? 'text-white' : 'text-gray-900'}`}>
                  <div className="flex items-end justify-center gap-1">
                    <span className="text-3xl font-bold">€</span>
                    <span className="text-6xl font-bold">{plan.price}</span>
                    <span className={`text-lg mb-2 ${plan.recommended ? 'text-blue-100' : 'text-gray-600'}`}>
                      /{plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.recommended ? 'text-blue-200' : 'text-blue-600'
                      }`} />
                      <span className={`text-sm ${plan.recommended ? 'text-white' : 'text-gray-700'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                  {plan.limitations.map((limitation, i) => (
                    <li key={`lim-${i}`} className="flex items-start gap-2 opacity-60">
                      <span className="text-sm">❌ {limitation}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(`/register?plan=${plan.plan_key}`)}
                  className={`w-full py-3 rounded-lg font-semibold transition ${
                    plan.recommended
                      ? 'bg-white text-blue-600 hover:bg-blue-50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Start Free Trial
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 text-gray-600">
            <p>All plans include 14-day free trial • No credit card required • Cancel anytime</p>
            <p className="mt-2">Need more? Contact us for custom enterprise pricing</p>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              See I-Match <span className="text-blue-600">In Action</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From lead import to personalized outreach in just 5 simple steps
            </p>
          </div>

          <div className="space-y-12">
            {demoSteps.map((step, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
              >
                <div className="flex-1">
                  <div className="text-8xl mb-4">{step.image}</div>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                  </div>
                  <p className="text-lg text-gray-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button
              onClick={() => navigate('/register?plan=tier1')}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-semibold transition shadow-xl shadow-blue-500/50 text-lg"
            >
              Try It Free For 14 Days
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-blue-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about I-Match CRM
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  {expandedFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                
                {expandedFaq === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600">
              Still have questions? <a href="mailto:support@imatch-crm.com" className="text-blue-600 hover:text-blue-700 font-medium">Contact our team</a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Sales Process?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join hundreds of sales teams using I-Match CRM to close more deals with AI-powered insights
          </p>
          <button
            onClick={() => navigate('/register?plan=tier1')}
            className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold transition shadow-xl text-lg"
          >
            Start Your Free 14-Day Trial
          </button>
          <p className="mt-4 text-blue-100">No credit card required • Full access to all features</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Target className="w-6 h-6 text-blue-400" />
                <span className="text-xl font-bold">I-Match CRM</span>
              </div>
              <p className="text-gray-400 text-sm">
                AI-powered CRM for modern sales teams
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="#demo" className="hover:text-white transition">Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
                <li><a href="/contact" className="hover:text-white transition">Contact</a></li>
                <li><a href="/documentation" className="hover:text-white transition">Documentation</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition">GDPR</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 I-Match CRM. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Welcome Back</h2>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              navigate('/login');
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="you@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full mt-6 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
              >
                Sign In
              </button>
            </form>
            
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  navigate('/register?plan=tier1');
                }}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                Don't have an account? Start free trial
              </button>
            </div>
            
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
